import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Separate keys so this PWA installs/authenticates independently of the shop app.
const TOKEN_KEY = "visitor_token";
const USER_KEY = "visitor_user";
const EMAIL_KEY = "visitor_email";
const PERMS_KEY = "visitor_perms";
const EXH_KEY = "visitor_exhibition";

export const vAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  },
  getPerms: () => {
    try {
      return JSON.parse(localStorage.getItem(PERMS_KEY) || "[]");
    } catch {
      return [];
    }
  },
  getRememberedEmail: () => localStorage.getItem(EMAIL_KEY),
  getExhibition: () => localStorage.getItem(EXH_KEY) || "",
  setExhibition: (v) => localStorage.setItem(EXH_KEY, v || ""),
  setAuth: (token, user, perms) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(PERMS_KEY, JSON.stringify(perms || []));
    if (user?.email) localStorage.setItem(EMAIL_KEY, user.email);
  },
  setPerms: (perms) => localStorage.setItem(PERMS_KEY, JSON.stringify(perms || [])),
  can: (perm) => vAuth.getPerms().includes(perm),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMS_KEY);
  },
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${vAuth.getToken()}` },
});

export const vapi = {
  login: (email, password) =>
    axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`, authHeaders()).then((r) => r.data),
  permissions: () =>
    axios.get(`${API}/auth/me/permissions`, authHeaders()).then((r) => r.data),

  // PIN auth (reuses the existing server-side PIN system)
  pinStatus: () => axios.get(`${API}/auth/pin/status`, authHeaders()).then((r) => r.data),
  setPin: (pin) => axios.post(`${API}/auth/pin/set`, { pin }, authHeaders()).then((r) => r.data),
  clearPin: () => axios.post(`${API}/auth/pin/clear`, {}, authHeaders()).then((r) => r.data),
  loginPin: (email, pin) =>
    axios.post(`${API}/auth/pin/login`, { email, pin }).then((r) => r.data),

  // WebAuthn — Face ID / Touch ID / fingerprint (passkeys)
  webauthnStatus: () =>
    axios.get(`${API}/auth/webauthn/status`, authHeaders()).then((r) => r.data),
  webauthnAvailable: (email) =>
    axios.get(`${API}/auth/webauthn/available?email=${encodeURIComponent(email)}`).then((r) => r.data),
  webauthnRegisterBegin: () =>
    axios.post(`${API}/auth/webauthn/register/begin`, { origin: window.location.origin }, authHeaders()).then((r) => r.data),
  webauthnRegisterComplete: (response) =>
    axios.post(`${API}/auth/webauthn/register/complete`, { response, origin: window.location.origin }, authHeaders()).then((r) => r.data),
  webauthnClear: () =>
    axios.delete(`${API}/auth/webauthn/credentials`, authHeaders()).then((r) => r.data),
  webauthnLoginBegin: (email) =>
    axios.post(`${API}/auth/webauthn/login/begin`, { email, origin: window.location.origin }).then((r) => r.data),
  webauthnLoginComplete: (email, response) =>
    axios.post(`${API}/auth/webauthn/login/complete`, { email, response, origin: window.location.origin }).then((r) => r.data),

  scanCard: (image_base64) =>
    axios.post(`${API}/visitors/scan-card`, { image_base64 }, authHeaders()).then((r) => r.data),

  scanBadge: (payload) =>
    axios.post(`${API}/visitors/scan-badge`, { payload }, authHeaders()).then((r) => r.data),

  create: (payload) =>
    axios.post(`${API}/visitors`, payload, authHeaders()).then((r) => r.data),

  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    const qs = q.toString();
    return axios.get(`${API}/visitors${qs ? `?${qs}` : ""}`, authHeaders()).then((r) => r.data);
  },

  get: (id) => axios.get(`${API}/visitors/${id}`, authHeaders()).then((r) => r.data),
  update: (id, payload) =>
    axios.put(`${API}/visitors/${id}`, payload, authHeaders()).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/visitors/${id}`, authHeaders()).then((r) => r.data),
  stats: (scope = "auto") =>
    axios.get(`${API}/visitors/stats?scope=${scope}`, authHeaders()).then((r) => r.data),

  checkDuplicate: ({ email, phone }) => {
    const q = new URLSearchParams();
    if (email) q.set("email", email);
    if (phone) q.set("phone", phone);
    return axios.get(`${API}/visitors/check-duplicate?${q.toString()}`, authHeaders()).then((r) => r.data);
  },

  cardObjectUrl: async (image_id) => {
    const res = await axios.get(`${API}/visitors/cards/${image_id}?t=${Date.now()}`, {
      ...authHeaders(),
      responseType: "blob",
    });
    return URL.createObjectURL(res.data);
  },
};

/**
 * Read an image File, downscale it, and return raw base64 (no data: prefix).
 * Keeps phone-camera photos small enough for the proxy + faster OCR.
 */
export function fileToCompressedBase64(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not load image"));
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl.split(",")[1]);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
